import { DatePicker, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppConfig } from './AppConfigProvider';

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * 表格过滤行组件
 * 
 * @param {Object} props - 组件属性
 * @param {Function} props.onFilterChange - 当过滤条件改变时的回调函数
 * @param {Object} props.filterValues - 当前的过滤值
 * @param {Function} props.onClearFilters - 清除所有过滤条件的回调函数
 * @returns {JSX.Element} 过滤行组件
 */
const FilterRow = memo(({ onFilterChange, filterValues = {}, onClearFilters, columns, showFilterRow, computedColumnWidths = {} }) => {
  const { theme } = useAppConfig();
  const { t } = useTranslation();
  // Local state for immediate input values (before debounce)
  const [localFilterValues, setLocalFilterValues] = useState(filterValues);

  // Update local state when props change
  useEffect(() => {
    console.log("FilterValues changed from props:", filterValues);
    setLocalFilterValues(prevValues => {
      // 避免不必要的更新
      if (JSON.stringify(prevValues) === JSON.stringify(filterValues)) {
        return prevValues;
      }
      return filterValues;
    });
  }, [filterValues]);

  /**
   * 防抖处理的过滤值更新函数
   */
  // Create a memoized debounced function
  const debouncedFilterChange = useCallback(
    debounce((newFilterValues) => {
      if (onFilterChange) {
        onFilterChange(newFilterValues);
      }
    }, 500),
    [] // Empty dependency array as the debounce function itself doesn't need to change
  );

  // Effect to clean up the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  /**
   * 处理过滤输入变化
   * @param {string} field - 字段名
   * @param {*} value - 新的值
   */
  const handleFilterChange = (field, value) => {
    console.log(`Filter change for ${field}:`, value);

    // Special handling for empty strings
    const newValue = value === '' ? undefined : value;

    const newFilterValues = { ...localFilterValues, [field]: newValue };
    console.log('Updated filter values:', newFilterValues);

    // Update local state immediately for responsive UI
    setLocalFilterValues(newFilterValues);

    // Debounce the actual filter change that triggers API calls
    debouncedFilterChange(newFilterValues);
  };

  /**
   * 清除所有过滤条件
   */
  const handleClearAll = () => {
    setLocalFilterValues({});

    // 调用防抖函数来触发过滤器更新
    debouncedFilterChange({});

    // if (onClearFilters) {
    //   onClearFilters();
    // }
  };

  /**
   * 处理日期变化
   * 转换为OData兼容的日期格式
   */
  const handleDateChange = (dates, dateStrings, field) => {
    console.log(`Date range change for ${field}:`, dates, dateStrings);

    // 如果dates为null，说明用户清除了选择
    if (!dates) {
      console.log(`Clearing date range for ${field}`);
      handleFilterChange(field, undefined);
      return;
    }

    // 检查是否有有效的开始和结束日期
    if (dates && dates[0] && dates[1]) {
      // Format dates correctly for OData datetime compatibility
      const startDate = dates[0].format('YYYY-MM-DD');
      const endDate = dates[1].format('YYYY-MM-DD');
      console.log(`Setting date range for ${field}:`, startDate, endDate);

      // Store as an object with start and end properties
      handleFilterChange(field, { start: startDate, end: endDate });
    } else {
      // 如果有任何一个日期未选择，则不设置过滤
      console.log(`Incomplete date range for ${field}, clearing filter`);
      handleFilterChange(field, undefined);
    }
  };

  console.log(localFilterValues, "localFilterValues")

  const filterFields = useMemo(() => {
    console.log("Creating filter fields with values:", localFilterValues);

    // 辅助函数来检查日期范围值是否有效
    const getDateRangeValue = (dateRange) => {
      if (!dateRange) return null;

      try {
        // 确保是对象且有start和end属性
        if (typeof dateRange === 'object' && dateRange.start && dateRange.end) {
          const startDayjs = dayjs(dateRange.start);
          const endDayjs = dayjs(dateRange.end);

          // 验证是否为有效的dayjs对象
          if (startDayjs.isValid() && endDayjs.isValid()) {
            return [startDayjs, endDayjs];
          }
        }
      } catch (error) {
        console.error("Error parsing date range:", error);
      }

      return null;
    };

    const components = [
      <RangePicker
        placeholder={[t('common.startDate'), t('common.endDate')]}
        value={getDateRangeValue(localFilterValues.postDate)}
        onChange={(dates, dateStrings) => handleDateChange(dates, dateStrings, 'postDate')}
        style={{ width: '80%' }}
        size="small"
        format="YYYY-MM-DD"

        title={t('invoice.postDate')}
        allowClear
        showToday={false}
      />, <Input
        placeholder={t('invoice.erpInvoiceId')}
        style={{ width: '80%' }}
        value={localFilterValues.id}
        onChange={(e) => handleFilterChange('id', e.target.value)}
        size="small"
        allowClear
        title={t('invoice.erpInvoiceId')}
      />,
      <Select
        placeholder={t('invoice.fapiaoType')}
        value={localFilterValues.type}
        onChange={(value) => handleFilterChange('type', value)}
        style={{ width: '80%' }}
        size="small"
        allowClear
        title={t('invoice.fapiaoType')}
      >
        <Option value="0">{t('invoice.specialVat')}</Option>
        <Option value="1">{t('invoice.generalVat')}</Option>
      </Select>,
      <Input
        placeholder={t('invoice.customerName')}
        style={{ width: '80%' }}
        value={localFilterValues.customerName}
        onChange={(e) => handleFilterChange('customerName', e.target.value)}
        size="small"
        allowClear
        title={t('invoice.customerName')}
      />,
      <Input
        placeholder={t('invoice.invoiceAmount')}
        style={{ width: '80%' }}
        value={localFilterValues.amount}
        onChange={(e) => handleFilterChange('amount', e.target.value)}
        size="small"
        allowClear
        type="number"
        title={t('invoice.invoiceAmount')}
      />, <Input
        placeholder={t('invoice.comment')}
        style={{ width: '80%' }}
        value={localFilterValues.comment}
        onChange={(e) => handleFilterChange('invoiceComment', e.target.value)}
        size="small"
        allowClear
        title={t('invoice.comment')}
      />,


      <Select
        placeholder={t('invoice.status')}
        value={localFilterValues.status}
        onChange={(value) => handleFilterChange('status', value)}
        style={{ width: '80%' }}
        size="small"
        allowClear
        title={t('invoice.status')}
      >
        <Option value="SUBMITTED">{t('sidebar.submitted')}</Option>
        <Option value="PENDING">{t('sidebar.pending')}</Option>
        <Option value="ERROR">{t('sidebar.error')}</Option>
        {/* <Option value="RED_NOTE">{t('sidebar.redNote')}</Option> */}
      </Select>,

      <Input
        placeholder={t('invoice.einvoiceId')}
        style={{ width: '80%' }}
        value={localFilterValues.einvoiceId}
        onChange={(e) => handleFilterChange('einvoiceId', e.target.value)}
        size="small"
        allowClear
        title={t('invoice.einvoiceId')}
      />,


      <Select
        placeholder={t('invoice.einvoicePdf')}
        value={localFilterValues.hasPdf === undefined ? undefined : String(localFilterValues.hasPdf)}
        onChange={(value) => handleFilterChange('hasPdf', value === undefined ? undefined : value === 'true')}
        style={{ width: '80%' }}
        size="small"
        allowClear
        title={t('invoice.einvoicePdf')}
      >
        <Option value="true">{t('common.yes')}</Option>
        <Option value="false">{t('common.no')}</Option>
      </Select>,

      <RangePicker
        placeholder={[t('common.startDate'), t('common.endDate')]}
        value={getDateRangeValue(localFilterValues.einvoiceDate)}
        onChange={(dates, dateStrings) => handleDateChange(dates, dateStrings, 'einvoiceDate')}
        style={{ width: '80%' }}
        size="small"
        format="YYYY-MM-DD"
        title={t('invoice.einvoiceDate')}
        allowClear
        showToday={false}
      />,

      <Input
        placeholder={t('invoice.submittedBy')}
        style={{ width: '80%' }}
        value={localFilterValues.submittedBy}
        onChange={(e) => handleFilterChange('submittedBy', e.target.value)}
        size="small"
        allowClear
        title={t('invoice.submittedBy')}
      />
    ]
    return columns.map((column, index) => {
      return {
        key: column.key,
        title: column.title,
        width: computedColumnWidths[column.key] || column.width,
        component: components[index]
      }
    })
  }, [localFilterValues, handleDateChange, t, computedColumnWidths])

  return (
    <div style={{ display: showFilterRow ? 'flex' : 'none' }} className="filter-row">
      <style>
        {`

        .ant-input-outlined:focus,   
        .ant-input-outlined:focus-within,
        .ant-input-outlined:hover,
        .ant-input-outlined:active,
        .ant-input-outlined:focus-visible {
          border-color: ${theme.primary} !important;
          box-shadow: 0 0 0 2px ${theme.primary}33 !important;
        }
  
          
          .ant-select:not(.ant-select-disabled):not(.ant-select-customize-input) .ant-select-selector:focus,
          .ant-select:not(.ant-select-disabled):not(.ant-select-customize-input) .ant-select-selector:focus-within {
            border-color: ${theme.primary} !important;
            box-shadow: 0 0 0 2px ${theme.primary}33 !important;
          }
          
          .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input) .ant-select-selector {
            border-color: ${theme.primary} !important;
            box-shadow: 0 0 0 2px ${theme.primary}33 !important;
          }

          .ant-select-outlined:focus,
          .ant-select-outlined:focus-within,
          .ant-select-outlined:hover,
          .ant-select-outlined:active,
          .ant-select-outlined:focus-visible {
            border-color: ${theme.primary} !important;
            box-shadow: 0 0 0 2px ${theme.primary}33 !important;
          }

          .ant-select-selector:hover {
            border-color: ${theme.primary} !important;
          }
          
          .ant-picker-outlined:focus,
          .ant-picker-outlined:focus-within,
          .ant-picker-outlined:hover,
          .ant-picker-outlined:active,
          .ant-picker-outlined:focus-visible {
            border-color: ${theme.primary} !important;
            box-shadow: 0 0 0 2px ${theme.primary}33 !important;
          }
          

          .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner, 
          .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner, 
          .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
            background: ${theme.primary} !important;
          }
          
          .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
            border-color: ${theme.primary} !important;
          }
        `}
      </style>
      <div className="table-row" style={{
        transition: 'none', // 禁用过渡动画以提高滚动性能
        willChange: 'transform' // 优化GPU加速
      }}>
        <div style={{
          display: 'flex',
          flex: computedColumnWidths[0] ? `0 0 ${computedColumnWidths[0]}px` : "0 0 48px",
          minWidth: 0,
          justifyContent: 'center',
          alignItems: 'center'
        }} className="cell cell-checkbox">
          <div className="icon">
            <span style={{ color: '#808080', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">
              filter_alt
            </span>
          </div>
        </div>

        <div style={{
          display: 'flex',
          flex: computedColumnWidths[1] ? `0 0 ${computedColumnWidths[1]}px` : "0 0 32px",
          minWidth: 0,
          justifyContent: 'center',
          alignItems: 'center'
        }} className="cell cell-expand">
          <div className="icon" onClick={handleClearAll} style={{ cursor: 'pointer' }}>
            <span style={{ color: '#808080', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">
              cancel
            </span>
          </div>
        </div>
        {
          filterFields.map((item, index) => {
            // 计算列的索引（加上展开列和选择列）
            const columnIndex = index + 2;
            const computedWidth = computedColumnWidths[columnIndex];

            return (
              <div
                key={item.key}
                style={{
                  flex: computedWidth ? `0 0 ${computedWidth}px` : `0 0 ${item.width}px`,
                  minWidth: 0
                }}
                className="filter-cell"
              >
                {item.component}
              </div>
            );
          })
        }


        {/* <div className="cell-id filter-cell">
          <Input
            placeholder="ERP Invoice ID"
            style={{ width: '80%' }}
            value={localFilterValues.id}
            onChange={(e) => handleFilterChange('id', e.target.value)}
            size="small"
            allowClear
            title="Filter by invoice ID"
          />
        </div>

        <div className="cell-type filter-cell">
          <Select
            placeholder="Fapiao Type"
            value={localFilterValues.type}
            onChange={(value) => handleFilterChange('type', value)}
            style={{ width: '80%' }}
            size="small"
            allowClear
            title="Filter by fapiao type"
          >
            <Option value="增值税专用发票">增值税专用发票</Option>
            <Option value="普通发票">普通发票</Option>
          </Select>
        </div>

        <div className="cell-customer filter-cell">
          <Input
            placeholder="Customer Name"
            style={{ width: '80%' }}
            value={localFilterValues.customerName}
            onChange={(e) => handleFilterChange('customerName', e.target.value)}
            size="small"
            allowClear
            title="Filter by customer name"
          />
        </div>

        <div className="cell-amount filter-cell">
          <Input
            placeholder="Invoice Amount"
            style={{ width: '80%' }}
            value={localFilterValues.amount}
            onChange={(e) => handleFilterChange('amount', e.target.value)}
            size="small"
            allowClear
            type="number"
            title="Filter by exact amount"
          />
        </div>

        <div className="cell-comment filter-cell">
          <Input
            placeholder="Comment"
            style={{ width: '80%' }}
            value={localFilterValues.comment}
            onChange={(e) => handleFilterChange('comment', e.target.value)}
            size="small"
            allowClear
            title="Filter by comment text"
          />
        </div>

        <div className="cell-status filter-cell">
          <Select
            placeholder="Status"
            value={localFilterValues.status}
            onChange={(value) => handleFilterChange('status', value)}
            style={{ width: '80%' }}
            size="small"
            allowClear
            title="Filter by invoice status"
          >
            <Option value="SUBMITTED">Submitted</Option>
            <Option value="PENDING">Pending</Option>
            <Option value="ERROR">Error</Option>
            <Option value="RED_NOTE">Red Note</Option>
          </Select>
        </div>

        <div className="cell-einvoice-id filter-cell">
          <Input
            placeholder="E-Invoice ID"
            style={{ width: '80%' }}
            value={localFilterValues.einvoiceId}
            onChange={(e) => handleFilterChange('einvoiceId', e.target.value)}
            size="small"
            allowClear
            title="Filter by e-invoice ID"
          />
        </div>

        <div className="cell-pdf filter-cell">
          <Select
            placeholder="E-Invoice PDF"
            value={localFilterValues.hasPdf === undefined ? undefined : String(localFilterValues.hasPdf)}
            onChange={(value) => handleFilterChange('hasPdf', value === undefined ? undefined : value === 'true')}
            style={{ width: '80%' }}
            size="small"
            allowClear
            title="Filter by PDF availability"
          >
            <Option value="true">Yes</Option>
            <Option value="false">No</Option>
          </Select>
        </div>

        <div className="cell-einvoice-date filter-cell">
          <DatePicker
            placeholder="E-Invoice Date"
            value={localFilterValues.einvoiceDate ? dayjs(localFilterValues.einvoiceDate) : null}
            onChange={(date, dateString) => handleDateChange(date, dateString, 'einvoiceDate')}
            style={{ width: '80%' }}
            size="small"
            format="YYYY-MM-DD"
            title="Filter by e-invoice date"
          />
        </div>

        <div className="cell-submitted-by filter-cell">
          <Input
            placeholder="E-Invoice Submitted By"
            style={{ width: '80%' }}
            value={localFilterValues.submittedBy}
            onChange={(e) => handleFilterChange('submittedBy', e.target.value)}
            size="small"
            allowClear
            title="Filter by submission user"
          />
        </div> */}
      </div>
    </div>
  );
});

FilterRow.propTypes = {
  onFilterChange: PropTypes.func,
  filterValues: PropTypes.object,
  onClearFilters: PropTypes.func
};

export default FilterRow; 